import React, { useRef } from "react";
import { X, Hash, Mail, Bell, Megaphone, Tag, CheckCircle, XCircle, FileText } from "lucide-react";
import EmailHtmlPreviewFrame from "./template-wizard/EmailHtmlPreviewFrame";

const EMAIL_TYPE_STYLE = {
  AUTH:                 { bg: "bg-info_bg",      text: "text-info",           border: "border-info_border" },
  PROJECT_NOTIFICATION: { bg: "bg-primary_tint", text: "text-primary",        border: "border-primary/20" },
  HELP:                 { bg: "bg-warning_bg",   text: "text-warning",        border: "border-warning_border" },
  CHAT_MESSAGE:         { bg: "bg-success_bg",   text: "text-success",        border: "border-success_border" },
  RESET_PASSWORD_OTP:   { bg: "bg-high_bg",      text: "text-high",           border: "border-high_border" },
  OTHER:                { bg: "bg-surface_alt",  text: "text-text_secondary", border: "border-border" },
};

const EmailTemplatePreview = ({ template, onClose }) => {
  const overlayRef = useRef(null);

  if (!template) return null;

  const typeStyle = EMAIL_TYPE_STYLE[template.email_type] || EMAIL_TYPE_STYLE.OTHER;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black/60 z-[1999] flex items-center justify-center p-4"
    >
      <div className="bg-surface w-full max-w-4xl rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-border">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-border bg-primary rounded-t-xl flex-shrink-0">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-white/80" />
              <h2 className="text-sm font-semibold text-white">{template.template_name}</h2>
            </div>
            <div className="flex items-center gap-1.5 text-white/60 text-xs font-mono">
              <Hash size={11} />
              {template.template_key}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors mt-0.5"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Meta Bar ───────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-border bg-background flex-shrink-0">

          {/* Subject */}
          <div className="flex items-center gap-1.5 text-xs text-text_secondary mr-2">
            <Mail size={12} className="text-text_muted" />
            <span className="font-medium text-text_primary">{template.subject}</span>
          </div>

          {/* Mailing For */}
          {template.mailing_for && (
            template.mailing_for === "NOTIFICATIONS" ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-info_bg text-info rounded text-[11px] font-medium border border-info_border">
                <Bell size={10} /> Notifications
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary_tint text-secondary rounded text-[11px] font-medium border border-secondary/20">
                <Megaphone size={10} /> Marketing
              </span>
            )
          )}

          {/* Email Type */}
          {template.email_type && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border}`}>
              <Tag size={10} />
              {template.email_type.replace(/_/g, " ")}
            </span>
          )}

          {/* Status */}
          {template.is_activated ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success_bg text-success rounded text-[11px] font-medium border border-success_border ml-auto">
              <CheckCircle size={10} /> Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface_alt text-text_secondary rounded text-[11px] font-medium border border-border ml-auto">
              <XCircle size={10} /> Inactive
            </span>
          )}
        </div>

        {/* ── Eyebrow / Heading ──────────────────────────────────────── */}
        {(template.eyebrow || template.heading) && (
          <div className="px-5 py-3 border-b border-border bg-surface flex-shrink-0 space-y-0.5">
            {template.eyebrow && (
              <p className="text-[11px] uppercase tracking-widest text-text_muted font-semibold">
                {template.eyebrow}
              </p>
            )}
            {template.heading && (
              <p className="text-base font-bold text-text_primary">{template.heading}</p>
            )}
          </div>
        )}

        {/* ── HTML Preview ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden bg-surface_alt">
          <EmailHtmlPreviewFrame html={template.body_html} emptyLabel="No HTML content available." />
        </div>

      </div>
    </div>
  );
};

export default EmailTemplatePreview;
